import { useGSAP } from "@gsap/react";
import { CustomEase } from "gsap/dist/CustomEase";
import { gsap } from "gsap/dist/gsap";
import React, { useRef } from "react";

import { BigHeroBlock as Value } from "@reactivated";

import {
    HeroLayoutProps,
    HeroLayoutSettings,
    TextAlignment,
} from "@thelabnyc/thelabui/src/components/Flexihero/HeroLayout.interfaces";
import { HeroSet } from "@thelabnyc/thelabui/src/components/Flexihero/HeroSet";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { MediaChooserBlock } from "../MediaChooserBlock";
import { RichText } from "../RichText";

import styles from "./index.module.scss";

gsap.registerPlugin(CustomEase);

interface Props {
    media: React.JSX.Element | null;
    content: React.JSX.Element | null;
    content_alignment: Value["content_alignment"];
    text_color: Value["text_color"];
    overlay: Value["overlay"];
    animation: Value["animation"];
}

interface ContentAlignment {
    contentAlignPrimary: HeroLayoutSettings["contentAlignPrimary"];
    contentAlignSecondary: HeroLayoutSettings["contentAlignSecondary"];
}

const BigHero = ({
    content,
    media,
    content_alignment,
    text_color,
    overlay,
    animation,
}: Props) => {
    const ref = useRef<HTMLDivElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);

    useGSAP(() => {
        if (
            !ref.current ||
            !innerRef.current ||
            animation !== "animateCurtains"
        )
            return;

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: ref.current,
                start: "top 20%",
                toggleActions: "play reverse play reverse",
            },
        });

        tl.fromTo(
            innerRef.current,
            { clipPath: "inset(15%)" },
            {
                clipPath: "inset(0%)",
                ease: CustomEase.create(
                    "custom",
                    "M0,0 C0,0 0.084,-0.026 0.172,0.166 0.291,0.429 0.354,0.62 0.558,0.821 0.639,0.901 0.713,0.937 0.808,0.97 0.879,0.995 1,1 1,1 ",
                ),
                duration: 1,
            },
        );
    }, []);

    const sharedStyles = {
        rootClassName: concatClassNames([
            text_color === "dark" ? styles.rootDarkText : styles.rootLightText,
            media === null ? styles.gradient : undefined,
        ]),
        mediaClassName: overlay ? styles[overlay] : undefined,
        contentClassName: styles.content,
    };

    const getAlignment: () => ContentAlignment = () => {
        switch (content_alignment) {
            case "top-left":
                return {
                    contentAlignPrimary: "left/top",
                    contentAlignSecondary: "left/top",
                };
            case "bottom-left":
                return {
                    contentAlignPrimary: "right/bottom",
                    contentAlignSecondary: "left/top",
                };
            case "middle-left":
                return {
                    contentAlignPrimary: "middle",
                    contentAlignSecondary: "left/top",
                };
            default:
                return {
                    contentAlignPrimary: "middle",
                    contentAlignSecondary: "middle",
                };
        }
    };

    const getTextAlignment: () => TextAlignment = () => {
        switch (content_alignment) {
            case "top-left":
            case "bottom-left":
            case "middle-left":
                return "left";
            default:
                return "center";
        }
    };

    const desktopSettings: HeroLayoutProps = {
        content,
        media,
        settings: {
            heroDirection: "row",
            heroPlacement: "backgroundFull",
            mediaPlacement: "background",
            mediaAspectRatio: "stretch",
            textAlignment: getTextAlignment(),
            contentWidth: 6,
            minHeight: "100vh",
            ...getAlignment(),
        },
        styles: sharedStyles,
    };

    const laptopSettings: HeroLayoutProps = {
        ...desktopSettings,
        settings: {
            ...desktopSettings.settings,
            contentWidth: 6,
        },
    };

    const tabletSettings: HeroLayoutProps = {
        ...desktopSettings,
        settings: {
            ...desktopSettings.settings,
            contentWidth: 8,
        },
    };

    return (
        <div ref={ref}>
            <div ref={innerRef}>
                <HeroSet
                    layouts={{
                        desktop: desktopSettings,
                        laptop: laptopSettings,
                        tablet: tabletSettings,
                        mobile: {
                            content,
                            media,
                            settings: {
                                heroDirection: "row",
                                heroPlacement: "backgroundFull",
                                mediaPlacement: "background",
                                mediaAspectRatio: "stretch",
                                textAlignment: getTextAlignment(),
                                contentWidth: 12,
                                minHeight: "100vh",
                                ...getAlignment(),
                            },
                            styles: sharedStyles,
                        },
                    }}
                />
            </div>
        </div>
    );
};

export const BigHeroBlock = ({ value }: { value: Value }) => {
    const content = value.content ? (
        <RichText
            value={value.content}
            largeHeadings={value.text_size === "textSizeLarge"}
        />
    ) : null;

    const media =
        value.media && value.media.length > 0 ? (
            <MediaChooserBlock value={value.media} />
        ) : null;

    return (
        <BigHero
            content={content}
            media={media}
            content_alignment={value.content_alignment}
            text_color={value.text_color}
            overlay={value.overlay}
            animation={value.animation}
        />
    );
};
